import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import styled from 'styled-components';
import { Next as NextIcon } from '@urbaninfrastructure/react-icons';
import { Input, Spinner, Flex, Box } from '@urbaninfrastructure/react-ui-kit';

const BUTTON_SIZE = 28;

export type InputInlineButtonState = 'default' | 'loading';

function Icon({ state }: { state?: InputInlineButtonState }) {
  const iconProps = { size: `${BUTTON_SIZE}px`, color: 'primaryIcon' };
  const isLoading = state === 'loading';
  const reduceMotion = useReducedMotion();
  return (
    <AnimatePresence>
      {!isLoading && (
        <motion.div
          key="arrow"
          initial={{ x: !reduceMotion ? 100 : undefined }}
          animate={{ x: 0 }}
          exit={{
            x: !reduceMotion ? 100 : undefined,
            opacity: reduceMotion ? 0 : undefined
          }}
          style={{ position: 'absolute' }}
          transition={{ ease: 'easeOut', duration: 0.3 }}
        >
          <NextIcon {...iconProps} display="block" />
        </motion.div>
      )}
      {isLoading && (
        <motion.div
          key="loading"
          style={{ position: 'absolute' }}
          initial={{ x: !reduceMotion ? -100 : undefined }}
          animate={{ x: 0 }}
          exit={{
            x: !reduceMotion ? -100 : undefined,
            opacity: reduceMotion ? 0 : undefined
          }}
          transition={{ ease: 'easeOut', duration: 0.3 }}
        >
          <Spinner display="block" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const IconButton = styled(Flex)`
  background: none;
  user-select: none;
  outline: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${BUTTON_SIZE}px;
  width: ${BUTTON_SIZE}px;
  overflow: hidden;
  border: 0;
  position: absolute;
  top: 50%;
  right: 1em;
  transform: translateY(-50%);
  &:hover,
  &:focus {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

export function InputInlineButton({
  inputProps,
  buttonProps,
  state
}: {
  inputProps?: Record<string, any>;
  buttonProps?: Record<string, any>;
  state?: InputInlineButtonState;
}) {
  return (
    <Box position="relative">
      <Input
        pr={`${BUTTON_SIZE}px`}
        bg="neutral.2"
        borderColor="transparent"
        {...inputProps}
      />
      <IconButton
        as="button"
        color="primaryIcon"
        p={0}
        disabled={state === 'loading'}
        {...buttonProps}
      >
        <Icon state={state} />
      </IconButton>
    </Box>
  );
}
